import { createContext, useState, useContext, ReactNode } from 'react';
import { v4 as uuidv4 } from 'uuid';

interface MessageContent {
  type: 'text' | 'image';
  text?: string;
  imageData?: string;
}

interface Message {
  from: 'user' | 'assistant';
  content: MessageContent[];
}

interface Chat {
  id: string;
  title: string;
  messages: Message[];
}

interface ChatContextType {
  currentChat: Chat;
  startNewChat: () => void;
  loadChat: (chat: Chat) => void;
  addMessage: (content: string | MessageContent[], from: 'user' | 'assistant') => void;
}

interface ChatProviderProps {
  children: ReactNode;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: ChatProviderProps) {
  const [currentChat, setCurrentChat] = useState<Chat>({
    id: uuidv4(),
    title: 'New Chat',
    messages: []
  });

  const startNewChat = () => {
    // TODO: can we delete this shit?
    setCurrentChat({
      id: uuidv4(),
      title: 'New Chat',
      messages: [{ 
        from: 'assistant', 
        content: [{ type: 'text', text: 'Hello! How can I help you today?' }] 
      }]
    });
  };

  const loadChat = (chat: Chat) => {
    setCurrentChat(chat);
  };

  const addMessage = (content: string | MessageContent[], from: 'user' | 'assistant') => {
    // Convert string content to MessageContent array
    const formattedContent = typeof content === 'string'
      ? [{ type: 'text' as const, text: content }]
      : content;
      
    setCurrentChat(prev => ({
      ...prev,
      messages: [...prev.messages, { from, content: formattedContent }]
    }));
  };

  return (
    <ChatContext.Provider
      value={{
        currentChat,
        startNewChat,
        loadChat,
        addMessage
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat(): ChatContextType {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
